import { Menu, Transition } from "@headlessui/react";
import { Fragment, ReactNode } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import { NavItemsType, NavTypeRecord } from "../layouts/DashboardLayout";
import { SystemType, UserRecord } from "../types/main";
import { NavSection } from "./NavSection";
import { LogoutGrayIcon, MobileLogoIcon } from "./ui-icons/header";
import {
  FilesIcon,
  NotificationIcon,
  ReportIcon,
  ShowHideArrowIcon,
  SidebarMenuBtnsIcon,
} from "./ui-icons/sidebar";
import { Button } from "./ui/Button";
import Avatar from "./ui/avatar";
import { selectCurrentUser } from "src/modules/users/selector";
import { nameFromEmail } from "src/utils/users.utils";
import { twMerge } from "tailwind-merge";
import { useAppSelector } from "../hooks/redux";
import { FlexCol, FlexRow } from "../utils/flexLayouts";
import { ListItem, quitNavigationItems } from "./Header";
import Loading from "./Loading";
import { HomeIcon, MobileConfigureIcon } from "./ui-icons/menu";
import HelpIcon from "./ui-icons/menu/HelpIcon";
import {
  FilePenIcon,
  MicrophoneIcon,
  MyTeamIcon,
  QuestionIcon,
} from "./ui-icons/menu/myTasks";
import SettingsIcon from "./ui-icons/sidebar/Settings";
import IncidentsIcon from "./ui-icons/menu/Incidents";
import IncidentsSecurityIcon from "./ui-icons/menu/IncidentsSecurityIcon";
import { selectItems } from "src/modules/spItems/selectors";
import { myTaskSettingsListName, settingsListName } from "src/api/config";
import { getSettings } from "src/pages/DashboardPage/gop/store/settings/selectors";
import { getSettingsVPO } from "src/pages/DashboardPage/vpo/store/settings/selectors";
import { CurrentUserType } from "src/modules/users/types";
import { gosSettingsSelector } from "src/pages/DashboardPage/gos/store/settings/selectors";
import Audit5SIcon from "./ui-icons/menu/myTasks/5sAudit";

const getQuestionNavigation = (id: string = "1") => [
  { name: "Помощь", url: `/dashboard/${id}/help`, icon: <HelpIcon /> },
];

// const quitNavigationItem: ListItem = { name: "Выйти", to: urlLogOut };

function Sidebar({
  sidebarOpen,
  toggleSidebar,
  activeSystem,
  list,
}: {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  activeSystem: SystemType | null;
  list: SystemType[];
}) {
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const settingsMT = useAppSelector((state) =>
    selectItems(state, myTaskSettingsListName)
  );
  const settingsVPO = useAppSelector(getSettingsVPO);
  const settingsGOS = useAppSelector(gosSettingsSelector);
  const settingsGOP = useAppSelector(getSettings);
  const settingsMD = useAppSelector((state) =>
    selectItems(state, settingsListName)
  );

  const { id } = useParams();

  if (!id) {
    navigate("/");
  }

  return (
    <div
      className={twMerge(
        sidebarOpen ? "w-80" : "w-[76px]",
        "mobile-menu flex  z-40 max-md:hidden flex-col transition-all h-screen fixed left-0 scrollbar-thin scrollbar-w-0.5 scrollbar-thumb-rounded-lg scrollbar-thumb-gray-400 p-4 pt-10 justify-between bg-white border-r font-medium"
      )}
    >
      <FlexRow>
        <FlexCol className="grow">
          <div
            className={twMerge(
              sidebarOpen ? "justify-between" : "justify-center",
              "flex flex-row items-center mb-6 h-6"
            )}
          >
            <FlexCol>
              {activeSystem === null || !currentUser.email ? (
                <Loading />
              ) : (
                <div
                  className={twMerge(
                    sidebarOpen ? "gap-x-2" : "",
                    "flex flex-row"
                  )}
                >
                  <FlexCol className="items-end">
                    <div className="w-6 h-6">
                      <img
                        className="object-contain w-full h-full"
                        src={activeSystem.url}
                        alt={activeSystem.title}
                        srcSet={activeSystem.url}
                      />
                    </div>
                  </FlexCol>
                  {sidebarOpen && <FlexCol>{activeSystem.title}</FlexCol>}
                </div>
              )}
            </FlexCol>
            {sidebarOpen && (
              <FlexCol className="whitespace-nowrap">
                <SystemMenu list={list} />
              </FlexCol>
            )}
          </div>
          <FlexRow>
            <nav
              className={twMerge(
                sidebarOpen ? "items-between" : "items-center",
                "flex flex-col divide-y grow"
              )}
            >
              <NavSection
                links={getNavigation(
                  id,
                  currentUser.isAdmin || currentUser.isFactoryAdmin,
                  currentUser.isSecurityAdmin || currentUser.isSecurityEditor,
                  currentUser,
                  settingsMT,
                  settingsVPO,
                  settingsGOS,
                  settingsGOP,
                  settingsMD
                )}
                mini={!sidebarOpen}
              />
              <NavSection
                links={getQuestionNavigation(id)}
                mini={!sidebarOpen}
              />

              {/* <NavSection links={questionNavigation} mini={!sidebarOpen} /> */}
            </nav>
          </FlexRow>
        </FlexCol>
      </FlexRow>
      <FlexRow>
        <FlexCol className={twMerge(sidebarOpen ? "grow" : "", "divide-y")}>
          <FlexRow
            className={twMerge(
              sidebarOpen ? "justify-between" : "justify-center",
              "items-center pb-[15px]"
            )}
          >
            <FlexCol>
              <FlexRow className="gap-2.5 items-end justify-center">
                <FlexCol>
                  <Avatar />
                </FlexCol>
                {sidebarOpen && (
                  <FlexCol className="text-[14px]">
                    <FlexRow
                      className="overflow-hidden font-medium text-ellipsis line-clamp-2"
                      title={currentUser.fullNameWithPatronymic}
                    >
                      {activeSystem === null || !currentUser.email ? (
                        <Loading />
                      ) : (
                        currentUser.fullNameWithPatronymic ||
                        nameFromEmail(currentUser.email)
                      )}
                    </FlexRow>
                    <FlexRow
                      className="font-normal text-primaryGray"
                      title={currentUser.jobTitleStr}
                    >
                      {currentUser.jobTitleStr}
                    </FlexRow>
                  </FlexCol>
                )}
              </FlexRow>
            </FlexCol>
            {sidebarOpen && (
              <FlexCol className="shrink-0">
                <MenuButton item={quitNavigationItems[0]}>
                  <LogoutGrayIcon />
                </MenuButton>
              </FlexCol>
            )}
          </FlexRow>

          <Button
            icon={<ShowHideArrowIcon />}
            text={"Свернуть"}
            mini={!sidebarOpen}
            className="h-10"
            classIcon={twMerge(
              sidebarOpen ? "" : "transform rotate-180",
              "transition-transform justify-center items-center"
            )}
            onClick={toggleSidebar}
          />
        </FlexCol>
      </FlexRow>
    </div>
  );
}

export default Sidebar;

function SystemMenu({ list }: { list: SystemType[] }) {
  return (
    <Menu as="div" className="flex relative text-left">
      {({ open }) => (
        <>
          <div>
            <Menu.Button
              className={twMerge(
                open ? "button-state-active" : "",
                "flex items-center rounded-lg button-state"
              )}
            >
              <SidebarMenuBtnsIcon />
            </Menu.Button>
          </div>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute top-0 z-10 origin-top-right ml-2.5 bg-white rounded-lg left-full shadow-custom ring-1 ring-black ring-opacity-5 focus:outline-none">
              <Menu.Item>
                <NavLink
                  to="/"
                  className="flex flex-row items-center p-5 gap-x-2 hover:bg-[#F5F7FD]"
                >
                  <div className="flex flex-col">
                    <MobileLogoIcon />
                  </div>
                </NavLink>
              </Menu.Item>
              {list.map((system) => (
                <Menu.Item key={system.id}>
                  <Link
                    to={system.externalLink || `/dashboard/${system.id}`}
                    className="flex flex-row items-center p-5 gap-x-2 hover:bg-[#F5F7FD]"
                  >
                    <div className="flex flex-col items-end">
                      <div className="w-6 h-6">
                        <img
                          className="object-contain w-full h-full"
                          src={system.url}
                          alt={system.title}
                          srcSet={system.url}
                        />
                      </div>
                    </div>
                    <div className="flex flex-col w-5/6 font-medium">
                      {system.title}
                    </div>
                  </Link>
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
}

function MenuButton({
  item,
  children,
}: {
  item: ListItem;
  children: ReactNode;
}) {
  return (
    <Menu as="div" className="flex relative z-10 items-center">
      {({ open }) => (
        <div className="relative">
          <Menu.Button
            className={twMerge(
              open ? "button-state-active" : "",
              "flex relative justify-center items-center w-10 h-10 rounded-lg menu-button button-state"
            )}
          >
            {children}
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute left-full whitespace-nowrap top-0 z-10 w-fit py-2.5 origin-top-right bg-white rounded-md shadow-custom ring-1 ring-black ring-opacity-5 focus:outline-none">
              <Menu.Item>
                <Link
                  to={item.to}
                  onClick={item.onClick}
                  className={
                    "hover:bg-[#F5F7FD] hover:text-darkblue block px-5 py-2 text-gray-700"
                  }
                >
                  {item.name}
                </Link>
              </Menu.Item>
            </Menu.Items>
          </Transition>
        </div>
      )}
    </Menu>
  );
}

export function getNavigation(
  id: string | undefined,
  isAdmin: boolean = false,
  securityAdmin: boolean = false,
  currentUser: CurrentUserType | null = null,
  settingsMT?: any,
  settingsVPO?: any,
  settingsGOS?: any,
  settingsGOP?: any,
  settingsMD?: any
): NavItemsType {
  const mainNavigation: NavTypeRecord = {
    "1": [
      { name: "Главная", url: `/dashboard/${id}`, icon: <HomeIcon /> },
      {
        name: "Документы",
        url: `/dashboard/${id}/documents`,
        icon: <FilesIcon />,
      },
      {
        name: "Уведомления",
        url: `/dashboard/${id}/notification`,
        icon: <NotificationIcon />,
      },
      { name: "Отчеты", url: `/dashboard/${id}/reports`, icon: <ReportIcon /> },
    ],
    "2": [
      { name: "Главная", url: `/dashboard/${id}`, icon: <HomeIcon /> },
      { name: "Отчеты", url: `/dashboard/${id}/reports`, icon: <ReportIcon /> },
    ],
    "3": [
      { name: "Главная", url: `/dashboard/${id}`, icon: <HomeIcon /> },
      {
        name: "Совещания",
        url: `/dashboard/${id}/meetings`,
        icon: <MicrophoneIcon />,
      },
      {
        name: "5Почему",
        url: `/dashboard/${id}/5-whys`,
        icon: <QuestionIcon />,
      },
      {
        name: "5S Аудит",
        url: `/dashboard/${id}/5S-audit`,
        icon: <Audit5SIcon />,
      },
      {
        name: "Отчеты об отклонении (AR)",
        url: `/dashboard/${id}/deviation-reports`,
        icon: <SettingsIcon />,
      },
      {
        name: "Поведенческий аудит (SBO)",
        url: `/dashboard/${id}/behavioral-audit`,
        icon: <FilePenIcon />,
      },
      {
        name: (
          <div className="flex flex-col leading-tight whitespace-normal">
            <span>Инциденты и несоответствия</span>
          </div>
        ),
        url: `/dashboard/${id}/incidents-and-nonconformities`,
        icon: <IncidentsIcon />,
      },
      { name: "Отчеты", url: `/dashboard/${id}/reports`, icon: <ReportIcon /> },
    ],
    "4": [
      { name: "Главная", url: `/dashboard/${id}`, icon: <HomeIcon /> },
      { name: "Отчеты", url: `/dashboard/${id}/reports`, icon: <ReportIcon /> },
    ],
    "6": [
      { name: "Главная", url: `/dashboard/${id}`, icon: <HomeIcon /> },
      { name: "Отчеты", url: `/dashboard/${id}/reports`, icon: <ReportIcon /> },
    ],
  };

  //МЗ
  let myTeam = false;

  if (
    currentUser &&
    Object.keys(currentUser).length > 0 &&
    currentUser.id &&
    currentUser.reports &&
    currentUser.reports.length > 0
  ) {
    myTeam = true;
    const newArr = mainNavigation["3"].toSpliced(1, 0, {
      name: "Моя команда",
      url: `/dashboard/${id}/my-team`,
      icon: <MyTeamIcon />,
    });
    mainNavigation["3"] = [...newArr];
  }

  if (securityAdmin) {
    let index = (myTeam && 7) || 6;
    const newArr = mainNavigation["3"].toSpliced(index, 0, {
      name: (
        <div className="flex flex-col leading-tight whitespace-normal">
          <span>Инциденты по Security</span>
        </div>
      ),
      url: `/dashboard/${id}/incidents-security`,
      icon: <IncidentsSecurityIcon />,
    });
    mainNavigation["3"] = [...newArr];
  }
  if (
    currentUser &&
    Object.keys(currentUser).length > 0 &&
    settingsMT &&
    settingsMT.length > 0
  ) {
    const userGroupIds =
      currentUser.groups.map((g: { Id: number }) => g.Id) || [];
    const filteredSettings =
      settingsMT.filter((setting: any) =>
        setting.UsersGroup
          ? setting.UsersGroup.some((obj: any) => userGroupIds.includes(obj.ID))
          : false
      ) || [];
    if (filteredSettings.length > 0) {
      mainNavigation["3"] = [
        ...mainNavigation["3"],
        {
          name: "Настройки",
          url: `/dashboard/${id}/settings`,
          icon: <MobileConfigureIcon />,
        },
      ];
    }
  }

  //VPO
  if (
    currentUser &&
    Object.keys(currentUser).length > 0 &&
    settingsVPO &&
    settingsVPO.length > 0
  ) {
    const userGroupIds =
      currentUser.groups.map((g: { Id: number }) => g.Id) || [];

    const filteredSettings =
      settingsVPO.filter((setting: any) =>
        setting.UsersGroup
          ? setting.UsersGroup.some((obj: any) => userGroupIds.includes(obj.id))
          : false
      ) || [];

    if (filteredSettings.length > 0) {
      mainNavigation["6"] = [
        ...mainNavigation["6"],
        {
          name: "Настройки",
          url: `/dashboard/${id}/settings`,
          icon: <MobileConfigureIcon />,
        },
      ];
    }
  }
  //GOS
  if (
    currentUser &&
    Object.keys(currentUser).length > 0 &&
    settingsGOS &&
    settingsGOS.length > 0
  ) {
    const userGroupIds =
      currentUser.groups.map((g: { Id: number }) => g.Id) || [];
    const filteredSettings =
      settingsGOS.filter((setting: any) =>
        setting.UsersGroup
          ? setting.UsersGroup.some((obj: any) => userGroupIds.includes(obj.id))
          : false
      ) || [];
    if (filteredSettings.length > 0) {
      mainNavigation["4"] = [
        ...mainNavigation["4"],
        {
          name: "Настройки",
          url: `/dashboard/${id}/settings`,
          icon: <MobileConfigureIcon />,
        },
      ];
    }
  }

  //GOP
  if (
    currentUser &&
    Object.keys(currentUser).length > 0 &&
    settingsGOP &&
    settingsGOP.length > 0
  ) {
    const userGroupIds =
      currentUser.groups.map((g: { Id: number }) => g.Id) || [];
    const filteredSettings = settingsGOP.filter((setting: any) =>
      setting.UsersGroup
        ? setting.UsersGroup.some((obj: any) => userGroupIds.includes(obj.id))
        : false
    );
    if (filteredSettings.length > 0) {
      mainNavigation["2"] = [
        ...mainNavigation["2"],
        {
          name: "Настройки",
          url: `/dashboard/${id}/settings`,
          icon: <MobileConfigureIcon />,
        },
      ];
    }
  }

  //MD
  if (
    currentUser &&
    Object.keys(currentUser).length > 0 &&
    settingsMD &&
    settingsMD.length > 0
  ) {
    const userGroupIds =
      currentUser.groups.map((g: { Id: number }) => g.Id) || [];
    const filteredSettings =
      settingsMD.filter((setting: any) =>
        setting.UsersGroup
          ? setting.UsersGroup.some((obj: any) => userGroupIds.includes(obj.ID))
          : false
      ) || [];
    if (filteredSettings.length > 0) {
      mainNavigation["1"] = [
        ...mainNavigation["1"],
        {
          name: "Настройки",
          url: `/dashboard/${id}/settings`,
          icon: <MobileConfigureIcon />,
        },
      ];
    }
  }

  if (!id) return mainNavigation["1"];

  return mainNavigation[id];
}
